import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check } from "@phosphor-icons/react";
import { Brand } from "@/components/Brand";
import { SaleBanner, SaleBadge } from "@/components/SaleBanner";
import { getConfig } from "@/lib/api";
import { payWithRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";

const isIndia = () => /Calcutta|Kolkata/.test(Intl.DateTimeFormat().resolvedOptions().timeZone || "");
const fmt = (v, cur) => (cur === "inr" ? `₹${Number(v).toLocaleString("en-IN")}` : `$${Number.isInteger(v) ? v : Number(v).toFixed(2)}`);

function Segmented({ options, value, onChange, testId }) {
  return (
    <div className="inline-flex rounded-full bg-black/5 p-1" data-testid={testId}>
      {options.map(([id, label, extra]) => (
        <button key={id} data-testid={`${testId}-${id}`} onClick={() => onChange(id)} className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium sm:text-sm ${value === id ? "bg-white text-ink shadow-sm" : "text-mist"}`}>
          {label} {extra && <span className="rounded-full bg-[#DFF6E4] px-1.5 py-0.5 text-[10px] font-semibold text-[#0B6B2A]">{extra}</span>}
        </button>
      ))}
    </div>
  );
}

function PriceBlock({ plan, p, currency, interval, featured, sale }) {
  if (plan.id === "free") {
    return <div className="mt-4 flex items-end gap-1"><span className="font-display text-5xl font-semibold">{fmt(0, currency)}</span><span className={`mb-1.5 text-sm ${featured ? "text-white/60" : "text-mist"}`}>/7 days</span></div>;
  }
  const discounted = p.price !== p.original;
  const perMonth = interval === "yearly" ? (currency === "inr" ? Math.round(p.price / 12) : (p.price / 12).toFixed(2)) : null;
  return (
    <div className="mt-4">
      {discounted && <span data-testid={`original-price-${plan.id}`} className={`text-base line-through ${featured ? "text-white/50" : "text-mist"}`}>{fmt(p.original, currency)}</span>}
      <div className="flex items-end gap-1">
        <span className="font-display text-5xl font-semibold" data-testid={`price-${plan.id}`}>{fmt(p.price, currency)}</span>
        <span className={`mb-1.5 text-sm ${featured ? "text-white/60" : "text-mist"}`}>/{interval === "yearly" ? "year" : "month"}</span>
      </div>
      <p className={`mt-1 h-4 text-xs ${featured ? "text-white/60" : "text-mist"}`}>
        {perMonth ? `Just ${fmt(perMonth, currency)}/month, billed yearly` : discounted ? `${sale.name} price` : ""}
      </p>
    </div>
  );
}

export default function PricingPage() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(null);
  const [currency, setCurrency] = useState(isIndia() ? "inr" : "usd");
  const [interval, setInterval] = useState("yearly");
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { getConfig().then(setCfg).catch(() => {}); }, []);
  const rzpEnabled = !!cfg?.razorpay_enabled;
  const sale = cfg?.sale;

  const choose = async (plan) => {
    if (plan.id === "free" || !user) return navigate(user ? "/app" : "/login");
    if (!rzpEnabled) return toast.error("Payments aren't available right now. Please try again later.");
    setBusy(plan.id);
    payWithRazorpay(plan.id, interval, currency, {
      onSuccess: async () => { await refresh(); toast.success(`${plan.label} is active. Enjoy!`); navigate("/payment/success?provider=razorpay"); },
      onError: (m) => { toast.error(m); setBusy(null); },
      onClose: () => setBusy(null),
    });
  };

  return (
    <div className="stp-bg">
      <SaleBanner sale={sale} cta={false} />
      <header className="stp-glass sticky top-0 z-30 border-x-0 border-t-0 border-b border-black/5">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-5 sm:px-8">
          <Brand to={user ? "/app" : "/"} />
          <Link to={user ? "/app" : "/"} data-testid="pricing-back-link" className="rounded-full px-3 py-1.5 text-sm text-brand hover:underline">{user ? "Back to workspace" : "Home"}</Link>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl px-5 pb-28 pt-16 sm:px-8">
        <div className="text-center">
          {sale?.active && <div className="mb-4 flex justify-center"><SaleBadge sale={sale} /></div>}
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="font-display text-5xl font-semibold text-ink sm:text-6xl">Pick your plan.</motion.h1>
          <p className="mt-4 text-lg text-mist">Start with {!user && localStorage.getItem("stp_ref") ? 14 : 7} days free. {sale?.active ? `Save ${sale.percent}% this ${sale.name.replace(" Sale", "")}.` : "Upgrade whenever you're ready."}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Segmented testId="interval-switch" value={interval} onChange={setInterval} options={[["monthly", "Monthly"], ["yearly", "Yearly", `−${cfg?.yearly_discount_percent ?? 20}%`]]} />
            <Segmented testId="currency-switch" value={currency} onChange={setCurrency} options={[["inr", "₹ India"], ["usd", "$ International"]]} />
          </div>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="pricing-grid">
          {(cfg?.plans || []).map((plan, i) => {
            const featured = plan.id === "pro";
            const p = cfg.prices?.[plan.id]?.[interval]?.[currency];
            return (
              <motion.div key={plan.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} data-testid={`plan-${plan.id}`}
                className={`relative flex flex-col rounded-3xl p-8 ${featured ? "bg-ink text-white shadow-[0_30px_60px_-20px_rgba(0,0,0,0.4)]" : "stp-card text-ink"}`}>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display text-2xl font-semibold">{plan.label}</h3>
                  {plan.id !== "free" && sale?.active ? <SaleBadge sale={sale} /> : featured && <span className="rounded-full bg-brand px-3 py-1 text-[11px] font-semibold text-white">Most popular</span>}
                </div>
                <PriceBlock plan={plan} p={p || {}} currency={currency} interval={interval} featured={featured} sale={sale || {}} />
                <ul className="mt-7 flex-1 space-y-3">
                  {plan.features.map((f, idx) => (
                    <li key={idx} className={`flex items-start gap-2.5 text-[15px] ${featured ? "text-white/85" : "text-ink"}`}><Check size={17} weight="bold" className="mt-0.5 shrink-0 text-brand" /> {f}</li>
                  ))}
                </ul>
                <button data-testid={`choose-${plan.id}`} onClick={() => choose(plan)} disabled={busy === plan.id}
                  className={`mt-8 h-12 w-full rounded-full text-[15px] font-semibold transition-colors disabled:opacity-60 ${featured ? "bg-brand text-white hover:bg-brand-hover" : "bg-black/5 text-ink hover:bg-black/10"}`}>
                  {busy === plan.id ? "Opening checkout…" : plan.id === "free" ? "Start free trial" : `Get ${plan.label} ${interval === "yearly" ? "yearly" : "monthly"}`}
                </button>
              </motion.div>
            );
          })}
        </div>
        <p className="mt-10 text-center text-xs text-mist" data-testid="payment-provider-note">
          {currency === "inr" ? "Secure payment by Razorpay · UPI, cards, netbanking and wallets." : "Secure payment by Razorpay · international cards, charged in US dollars."}
        </p>
      </main>
    </div>
  );
}
